import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from 'src/utils/base-query.js'
import _ from 'lodash'
import { createSlice } from '@reduxjs/toolkit'

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState: [],
  reducers: {
    setWorkspace (state, action) {
      return action.payload
    }
  }
})

export const { setWorkspace } = workspaceSlice.actions

export const workspacesSlice = createSlice({
  name: 'workspaces',
  initialState: {
    uniqueWorkspaces: [],
    currentWorkspace: null,
    workspacePlan: null,
  },
  reducers: {
    setUniqueWorkspaces: (state, action) => {
      state.uniqueWorkspaces = action.payload
    },
    setCurrentWorkspace: (state, action) => {
      state.currentWorkspace = action.payload
    },
    setWorkspacePlan: (state, action) => {
      state.workspacePlan = action.payload
    },
    setAllWorkspacesData: (state, action) => {
      state.uniqueWorkspaces = action.payload.uniqueWorkspaces
      state.currentWorkspace = action.payload.currentWorkspace
      state.workspacePlan = action.payload.workspacePlan
    },
  },
})

export const {
  setUniqueWorkspaces,
  setCurrentWorkspace,
  setWorkspacePlan,
  setAllWorkspacesData,
} = workspacesSlice.actions

// Define a service using a base URL and expected endpoints
export const workSpaceApi = createApi({
  reducerPath: 'workSpaceApi',
  tagTypes: ['WORKSPACES'],
  baseQuery,
  endpoints: (builder) => ({
    createWorkSpace: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'workSpaces/add-workSpace',
          body: data
        }
      },
      invalidatesTags: ['WORKSPACES']
    }),
    getWorkSpaces: builder.mutation({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: 'workSpaces',
          params
        }
      }
    }),
    deleteWorkSpace: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `workSpaces/${id}`
        }
      },
      invalidatesTags: ['WORKSPACES']
    }),
    getCurrentWorkSpace: builder.mutation({
      query: (id) => {
        return {
          method: 'GET',
          url: `workSpaces/current/${id}`
        }
      },
      invalidatesTags: ['WORKSPACES']
    }),
    updateWorkSpaceName: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `workSpaces/${id}`,
          body: data
        }
      }
    }),
    updateWorkSpaceStatus: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'workSpaces/updateStatus',
          body: data
        }
      }
    }),
    sendInvitation: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          method: 'POST',
          url: `workSpaces/${id}/invitation`,
          body: data
        }
      }
    }),
    statusInvitation: builder.mutation({
      query: ({ id, ...data }) => {
        return {
          method: 'POST',
          url: `workSpaces/${id}/status-invitation`,
          body: data
        }
      }
    }),
    getWorkSpace: builder.mutation({
      query: ({ id, email }) => {
        return {
          method: 'GET',
          url: `workSpaces/${id}`,
          params: {
            email
          }
        }
      }
    }),
    getInvitation: builder.mutation({
      query: (id) => {
        return {
          method: 'GET',
          url: `workSpaces/${id}/get-invitation`
        }
      }
    }),
    getMembers: builder.mutation({
      query: (queryParams) => {
        const params = new URLSearchParams(_.pickBy(queryParams))
        return {
          method: 'GET',
          url: `workSpaces/${queryParams.status}/get-members`,
          params
        }
      }
    }),
    deleteMember: builder.mutation({
      query: (id) => {
        return {
          method: 'DELETE',
          url: `workSpaces/member/${id}`
        }
      }
    }),
    updateMemberRole: builder.mutation({
      query: ({ id, data }) => {
        return {
          method: 'PUT',
          url: `workSpaces/update-member/${id}`,
          body: data
        }
      }
    }),
    leaveWorkspace: builder.mutation({
      query: (data) => {
        return {
          method: 'POST',
          url: 'workSpaces/leave-workspace',
          body: data
        }
      }
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCreateWorkSpaceMutation,
  useGetWorkSpacesMutation,
  useDeleteWorkSpaceMutation,
  useUpdateWorkSpaceNameMutation,
  useUpdateWorkSpaceStatusMutation,
  useSendInvitationMutation,
  useGetWorkSpaceMutation,
  useStatusInvitationMutation,
  useGetInvitationMutation,
  useGetMembersMutation,
  useDeleteMemberMutation,
  useUpdateMemberRoleMutation,
  useGetCurrentWorkSpaceMutation,
  useLeaveWorkspaceMutation
} = workSpaceApi
